import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IThunkApi, TApplicationState } from '../../types/redux';
import { postSuperhostRequest } from '../../services/superhost';
import { TSuperhostSubmitError } from '../../services/superhost/types';

export type { TSuperhostSubmitError };

export interface ISuperhostState {
  initialStickerCount: number | null;
  formStatus: 'idle' | 'loading' | 'success' | 'error';
  errorCode: TSuperhostSubmitError | null;
}

interface ISubmitRequestParams {
  fullName: string;
  deliveryAddress: string;
  stickersCount: number;
}

export const initialState: ISuperhostState = {
  initialStickerCount: null,
  formStatus: 'idle',
  errorCode: null,
};

export const submitSuperhostRequest = createAsyncThunk<
  void,
  ISubmitRequestParams,
  IThunkApi & { rejectValue: TSuperhostSubmitError }
>('superhost/submitRequest', async (params, { getState, rejectWithValue, extra: context }) => {
  const userId = getState().user.userId;

  if (!userId) {
    return rejectWithValue('unauthorized');
  }

  try {
    const result = await postSuperhostRequest(context, { userId, ...params });

    if (result.statusCode === 400) {
      context.logger.error('[submitSuperhostRequest] backend returned 400', {
        userId,
        response: JSON.stringify(result.response),
        params: JSON.stringify(params),
      });

      return rejectWithValue(result.response.code);
    }

    return undefined;
  } catch (error) {
    context.logger.error(error as Error, {
      userId,
      params: JSON.stringify(params),
    });

    return rejectWithValue('unknown');
  }
});

const superhostSlice = createSlice({
  initialState,
  name: 'superhost',
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(submitSuperhostRequest.pending, state => {
        state.formStatus = 'loading';
        state.errorCode = null;
      })
      .addCase(submitSuperhostRequest.fulfilled, state => {
        state.formStatus = 'success';
      })
      .addCase(submitSuperhostRequest.rejected, (state, action) => {
        state.formStatus = 'error';
        state.errorCode = action.payload ?? 'unknown';
      });
  },
});

export const selectInitialStickerCount = (state: TApplicationState) => state.superhost.initialStickerCount;
export const selectFormStatus = (state: TApplicationState) => state.superhost.formStatus;
export const selectErrorCode = (state: TApplicationState) => state.superhost.errorCode;

export const { reducer: superhostReducer } = superhostSlice;
